import { LinkOutlined } from "@mui/icons-material";
import { Box, Chip, IconButton } from "@mui/material";
import RichTextInputWithoutCustomStyles from "atoms/RichTextInputWithoutCustomStyles";
import _omit from "lodash/omit";
import { pluralizeResource } from "misc/formatHelpers";
import { renderNameWithOrganization } from "misc/renderHelpers";
import { CustomFieldRecordType, CustomFieldSelectValueRecordType } from "pages/CustomField/CustomField.types";
import { FieldTypeEnum } from "pages/EventBase/EventBase.types";
import { AttendeeRecordType } from "pages/SharedRecords.types";
import { SyntheticEvent, useCallback } from "react";
import {
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanInput,
  CommonInputProps,
  DateInput,
  Link,
  NumberInput,
  ReferenceArrayInput,
  ReferenceInput,
  required,
  TextInput,
  useGetRecordRepresentation,
} from "react-admin";
import { useWatch } from "react-hook-form";

type CustomFieldInputPropsType = {
  field: CustomFieldRecordType;
  source?: string;
  label?: string;
  defaultValue?: number | string | boolean;
  readOnly?: boolean;
  hideLinks?: boolean;
  softRequiredAsRequired?: boolean;
};

export default function CustomFieldInput(props: CustomFieldInputPropsType) {
  const { field, source, label, defaultValue, readOnly = false, hideLinks = false, softRequiredAsRequired } = props;
  const { label: fieldLabel, slug, field_type, data_type, allow_multiple_select, soft_required } = field;

  const finalSource = source || `custom_fields_data.${slug}`;
  const fieldValue = useWatch({ name: finalSource }) as string;

  const commonProps: CommonInputProps = {
    source: finalSource,
    label: label || fieldLabel,
    variant: "outlined",
    defaultValue,
    readOnly,
    validate: softRequiredAsRequired && soft_required ? required() : undefined,
  };

  const sx = { width: "100%" };

  const prepareChoices = useCallback(
    (selectValues: CustomFieldSelectValueRecordType[]) => {
      const existingValues = new Set<string>();

      // Build choices from select_values
      const choices = selectValues.map((selectValue) => {
        existingValues.add(selectValue.value);
        return {
          id: selectValue.value,
          name:
            selectValue.value !== selectValue.name ? `${selectValue.name} (${selectValue.value})` : selectValue.name,
        };
      });

      // Handle old values that no longer exist in select_values
      if (fieldValue) {
        const valuesToCheck = Array.isArray(fieldValue) ? fieldValue : [fieldValue];

        valuesToCheck.forEach((val) => {
          if (val && !existingValues.has(val as string)) {
            choices.push({
              id: val as string,
              name: `Alter Wert: (${val})`,
            });
          }
        });
      }

      return choices;
    },
    [fieldValue],
  );

  switch (data_type) {
    case "SELECT": {
      const choices = prepareChoices(field.meta.select_values);

      if (allow_multiple_select) {
        return <AutocompleteArrayInput {...commonProps} choices={choices} sx={sx} />;
      }

      return <AutocompleteInput {...commonProps} choices={choices} sx={sx} />;
    }
    case "DATE":
      return <DateInput {...commonProps} sx={sx} />;
    case "NUMBER":
      // TODO: Somehow non numeric values are rendered here as well,
      // but react admin thinks the input is empty. So the label overlaps
      // the entered text.
      return <NumberInput {...commonProps} InputLabelProps={{ shrink: true }} sx={sx} />;
    case "BOOLEAN":
      return <BooleanInput {...commonProps} />;

    case "RELATION": {
      const resource = field.meta?.related_resource;
      if (!resource) return null;

      const pluralResource = pluralizeResource(resource);
      const optionText = pluralResource === "attendees" ? renderNameWithOrganization : undefined;

      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
          <ReferenceInput source={commonProps.source} reference={pluralResource} sx={{ flex: 1 }}>
            <AutocompleteInput
              label={commonProps.label}
              disabled={!!commonProps.readOnly}
              fullWidth
              {...(optionText ? { optionText } : {})}
            />
          </ReferenceInput>
          {!hideLinks && fieldValue && (
            <IconButton
              component={Link}
              to={`/${pluralResource}/${fieldValue}`}
              size="small"
              onClick={(e: SyntheticEvent) => e.stopPropagation()}
              sx={{ mb: 3 }}
            >
              <LinkOutlined fontSize="small" color="primary" />
            </IconButton>
          )}
        </Box>
      );
    }
    case "RELATION_MANY": {
      const resource = field.meta?.related_resource;
      if (!resource) return null;

      const pluralResource = pluralizeResource(resource);
      const getRecordRepresentation = useGetRecordRepresentation(pluralResource);
      const optionText = pluralResource === "attendees" ? renderNameWithOrganization : undefined;

      return (
        <ReferenceArrayInput source={commonProps.source} reference={pluralResource}>
          <AutocompleteArrayInput
            label={commonProps.label}
            disabled={!!commonProps.readOnly}
            fullWidth
            {...(optionText ? { optionText } : {})}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const label = optionText ? optionText(option as AttendeeRecordType) : getRecordRepresentation(option);
                const tagProps = _omit(getTagProps({ index }), "key");

                return hideLinks ? (
                  <Chip {...tagProps} key={option.id} label={label} />
                ) : (
                  <Chip
                    {...tagProps}
                    key={option.id}
                    label={label}
                    component={Link}
                    to={`/${pluralResource}/${option.id}`}
                    clickable
                    onClick={(e: SyntheticEvent) => {
                      e.stopPropagation();
                    }}
                  />
                );
              })
            }
          />
        </ReferenceArrayInput>
      );
    }

    case "TEXT":
    default:
      switch (field_type) {
        case FieldTypeEnum.TextArea:
          return <TextInput {...commonProps} multiline minRows={3} sx={sx} />;
        case FieldTypeEnum.Richtext:
          return <RichTextInputWithoutCustomStyles {...commonProps} sx={sx} />;
        case FieldTypeEnum.Text:
        default:
          return <TextInput {...commonProps} sx={sx} />;
      }
  }
}
